/**
 * ScentAlerts - Real-time alerts for unusual scent activity nearby
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocialStore } from '@/stores/useSocialStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { cn } from '@/utils/cn';
import type { ScentReport } from '@/types/social';

interface ScentAlert {
  id: string;
  type: 'warning' | 'info' | 'community';
  title: string;
  message: string;
  report?: ScentReport;
  distance?: number;
  timestamp: Date;
}

// Concerning scent keywords that trigger warnings
const WARNING_SCENTS = ['smoke', 'gas', 'chemical', 'industrial', 'burning', 'sewage', 'toxic', 'fumes'];

// Calculate distance between two coordinates (Haversine formula)
function getDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)}km`;
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffMs / 86400000)}d ago`;
}

export function ScentAlerts() {
  const feed = useSocialStore((state) => state.feed);
  const currentPosition = useLocationStore((state) => state.currentPosition);
  const [alerts, setAlerts] = useState<ScentAlert[]>([]);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  // Generate alerts from recent reports
  useEffect(() => {
    if (!currentPosition) return;

    const now = new Date().getTime();
    const oneHourAgo = now - 3600000;
    const newAlerts: ScentAlert[] = [];

    // Check recent reports
    feed.forEach((report) => {
      const reportTime = new Date(report.created_at).getTime();
      if (reportTime < oneHourAgo) return;
      if (!report.latitude || !report.longitude) return;

      const distance = getDistanceKm(
        currentPosition.lat,
        currentPosition.lng,
        report.latitude,
        report.longitude
      );

      // Only alert for reports within 5km
      if (distance > 5) return;

      const isWarning = WARNING_SCENTS.some((keyword) =>
        report.scent_notes?.toLowerCase().includes(keyword)
      );

      if (isWarning) {
        newAlerts.push({
          id: `warning-${report.id}`,
          type: 'warning',
          title: '⚠️ Unusual Scent Alert',
          message: `${report.scent_notes} reported ${formatDistance(distance)} away`,
          report,
          distance,
          timestamp: new Date(report.created_at),
        });
      } else if (distance < 1) {
        // Community alert for very nearby reports
        newAlerts.push({
          id: `community-${report.id}`,
          type: 'community',
          title: '☁️ Scent Nearby',
          message: `Someone reported "${report.scent_notes}" ${formatDistance(distance)} away`,
          report,
          distance,
          timestamp: new Date(report.created_at),
        });
      }
    });

    // Check for scent clusters (multiple reports in same area)
    const recentReports = feed.filter(
      (r) =>
        new Date(r.created_at).getTime() > oneHourAgo &&
        r.latitude &&
        r.longitude &&
        getDistanceKm(currentPosition.lat, currentPosition.lng, r.latitude!, r.longitude!) < 2
    );

    if (recentReports.length >= 3) {
      const clusterId = `cluster-${Date.now()}`;
      if (!newAlerts.find((a) => a.id.startsWith('cluster'))) {
        newAlerts.push({
          id: clusterId,
          type: 'info',
          title: '📍 Scent Hotspot',
          message: `${recentReports.length} scent reports in your area in the last hour`,
          timestamp: new Date(),
        });
      }
    }

    // Sort by timestamp (newest first) and limit
    newAlerts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    setAlerts(newAlerts.slice(0, 5));
  }, [feed, currentPosition]);

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter((alert) => !dismissedAlerts.has(alert.id));
  const displayAlerts = showAll ? visibleAlerts : visibleAlerts.slice(0, 2);

  const handleDismiss = (alertId: string) => {
    setDismissedAlerts((prev) => new Set([...prev, alertId]));
  };

  if (visibleAlerts.length === 0) return null;

  return (
    <div className="fixed top-4 left-4 z-40 w-80 space-y-2">
      <AnimatePresence>
        {displayAlerts.map((alert, index) => (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, x: -100, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -100, scale: 0.9 }}
            transition={{ delay: index * 0.1 }}
            className={cn(
              'relative p-4 rounded-xl border backdrop-blur-xl shadow-lg',
              alert.type === 'warning' &&
                'bg-red-500/10 border-red-500/30 shadow-red-500/10',
              alert.type === 'info' &&
                'bg-cyan-500/10 border-cyan-500/30 shadow-cyan-500/10',
              alert.type === 'community' &&
                'bg-emerald-500/10 border-emerald-500/30 shadow-emerald-500/10'
            )}
          >
            {/* Dismiss button */}
            <button
              onClick={() => handleDismiss(alert.id)}
              className="absolute top-2 right-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              ✕
            </button>

            {/* Alert content */}
            <div className="pr-6">
              <h4
                className={cn(
                  'font-semibold text-sm mb-1',
                  alert.type === 'warning' && 'text-red-400',
                  alert.type === 'info' && 'text-cyan-400',
                  alert.type === 'community' && 'text-emerald-400'
                )}
              >
                {alert.title}
              </h4>
              <p className="text-slate-300 text-sm">{alert.message}</p>

              {/* Meta info */}
              <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                <span>{getTimeAgo(alert.timestamp)}</span>
                {alert.report && alert.report.likes_count > 0 && (
                  <span>✓ {alert.report.likes_count} verified</span>
                )}
              </div>
            </div>

            {/* Warning pulse effect */}
            {alert.type === 'warning' && (
              <div className="absolute inset-0 rounded-xl border border-red-500/50 animate-pulse pointer-events-none" />
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Show more button */}
      {visibleAlerts.length > 2 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowAll(!showAll)}
          className="w-full py-2 text-xs text-slate-400 hover:text-white transition-colors"
        >
          {showAll
            ? 'Show less'
            : `+ ${visibleAlerts.length - 2} more alert${visibleAlerts.length - 2 > 1 ? 's' : ''}`}
        </motion.button>
      )}
    </div>
  );
}

export default ScentAlerts;
